import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { filter, isEmpty, map, some, sumBy } from 'lodash';
import React, { FunctionComponent, useState } from 'react';

import { DuplicateSlideGroup } from './duplicateSlideRows';

const GROUPS_PER_PAGE = 10;

interface DuplicateSlideRowsDialogProps {
  groups: DuplicateSlideGroup[];
  source: 'file' | 'plan';
  onApply: (keptOptionIdByGroupId: Record<string, string>) => void;
  onCancel: () => void;
}

export const DuplicateSlideRowsDialog: FunctionComponent<DuplicateSlideRowsDialogProps> = ({
  groups,
  source,
  onApply,
  onCancel,
}) => {
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const keptOptionId = (group: DuplicateSlideGroup) => {
    const chosen = choices[group.id];
    if (chosen !== undefined && some(group.options, { id: chosen })) return chosen;
    return group.isIdentical ? group.options[0].id : null;
  };
  const unresolvedCount = filter(groups, (group) => keptOptionId(group) === null).length;
  const removedRowCount = sumBy(groups, (group) => group.options.length - 1);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(groups.length / GROUPS_PER_PAGE) - 1));
  const visibleGroups = groups.slice(currentPage * GROUPS_PER_PAGE, (currentPage + 1) * GROUPS_PER_PAGE);

  const apply = () => {
    if (unresolvedCount > 0) return;
    const kept: Record<string, string> = {};
    groups.forEach((group) => {
      kept[group.id] = keptOptionId(group) as string;
    });
    onApply(kept);
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onCancel} aria-labelledby="duplicate-slide-rows-title">
      <DialogChromeHeader
        title="Resolve duplicate rows"
        subtitle="Each slide can have only one row in the case manifest. Choose the row to keep for every slide."
        titleId="duplicate-slide-rows-title"
        closeButtonAriaLabel="Close duplicate rows dialog"
        onClose={onCancel}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity={unresolvedCount > 0 ? 'info' : 'success'}>
            {groups.length === 1 ? '1 slide has' : `${groups.length} slides have`} duplicate rows.{' '}
            {unresolvedCount > 0
              ? `${unresolvedCount} still ${unresolvedCount === 1 ? 'needs' : 'need'} a choice.`
              : 'Choose one row for each slide. Several slides in one case are kept together.'}
          </Alert>
          {map(visibleGroups, (group) => {
            const kept = keptOptionId(group);
            return (
              <FormControl key={group.id} component="fieldset">
                <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {group.slideName}
                </FormLabel>
                {group.isIdentical && (
                  <Typography variant="body2" color="text.secondary">
                    These rows are identical, so either one can be kept.
                  </Typography>
                )}
                <RadioGroup
                  aria-label={`Row to keep for ${group.slideName}`}
                  value={kept ?? ''}
                  onChange={(event) => setChoices((previous) => ({ ...previous, [group.id]: event.target.value }))}
                >
                  {map(group.options, (option) => (
                    <Box key={option.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <FormControlLabel
                        value={option.id}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2">
                              {option.caseLabel} ({option.origin})
                            </Typography>
                            {!isEmpty(option.changes) && (
                              <Typography variant="caption" color="text.secondary">
                                {option.changes.join(' · ')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Chip size="small" variant="outlined" label="Duplicate" sx={{ mt: 1 }} />
                      {kept !== null && (
                        <Chip
                          size="small"
                          variant={kept === option.id ? 'filled' : 'outlined'}
                          color={kept === option.id ? 'success' : 'default'}
                          label={kept === option.id ? 'Kept' : 'Will be removed'}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>
            );
          })}
          {groups.length > GROUPS_PER_PAGE && (
            <TablePagination
              component="div"
              count={groups.length}
              page={currentPage}
              rowsPerPage={GROUPS_PER_PAGE}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => `Slides ${from}-${to} of ${count}`}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        <Button color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={unresolvedCount > 0} onClick={apply}>
          Keep selected row
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
